<script lang="ts">
import { getEnv } from 'config';
import { last } from 'lodash';
import { store as i18nStore } from 'plugins/i18n/store';
import { tryResolve } from 'router';
import { PropType, computed, defineComponent, ref } from 'vue';
import { useRouter } from 'vue-router';
import IconArrow from './assets/icon-arrow.svg?raw';
import IconBlank from './assets/icon-blank.svg?raw';
import IconDownload from './assets/icon-download.svg?raw';

const UiButton = defineComponent({
  name: "UiButton",
  props: {
    /**
      * Actual Label of the Button, will also feed the `title` value.
    */
    label: {
      type: String,
      default: ''
    },
    /**
      * Theme / level of the Button
    */
    theme: {
      type: String as PropType<ButtonTheme>,
      default: undefined
    },
    /**
      * Variant (control colors)
    */
    variant: {
      type: String as PropType<ExperienceVariant>,
      default: "default"
    },
    /**
     * Inverse Color in dark mode
    */
    dark: {
      type: Boolean,
      default: false
    },
    /**
      * Optional, if we need a title attr on the BUtton (for Icon btn or else.)
    */
    title: {
      type: String,
      default: undefined
    },
    /**
      * Optional, Force a specific tag on the element. (semantic purpose) Else everything will be computed (router-link, a, button)
    */
    tag: {
      type: String,
      default: undefined
    },
    /**
      * router link format
    */
    to: {
      type: [Object, String],
      default: undefined
    },
    /**
      * classic url href
    */
    href: {
      type: String,
      default: undefined
    },
    /**
      * same as href.
    */
    src: {
      type: String,
      default: undefined
    },
    /**
      * if we need a target -> '_blank'
    */
    target: {
      type: String,
      default: undefined
    },
    /**
      * for Custom icon
    */
    icon: {
      type: String,
      default: undefined
    },
    /**
      * for Custom icon when button is actif/selected
    */
    iconSelected: {
      type: String,
      default: undefined
    },
    /**
      * Set a selected state to the button
    */
    selected: {
      type: Boolean,
      default: false
    },
    /**
      * for Download link
    */
    download: {
      type: [Boolean, String],
      default: undefined
    },
    /**
      * display an icon or not.
    */
    arrow: {
      type: Boolean,
      default: false
    },
    /**
      * direction of the icon with the label
    */
    direction: {
      type: String as PropType<ButtonDirection>,
      default: 'right'
    },
  },
  setup(props) {
    const options = { ...props }
    const el = ref<O<any>>(undefined)

    // ensure some props
    if (!options.title) {
      options.title = props.label
    }

    if (options.href && !props.to && i18nStore.locale) {
      options.href = options.href.replace(getEnv(i18nStore.locale.env), i18nStore.locale.uri || '')
      const route = tryResolve(options.href)

      if (route && route.matched?.length > 0) {
        options.to = { path: options.href }
      } else {
        options.target = '_blank';
      }
    }

    if (!options.href
      && props.to
      && typeof props.to === 'object') {
      const route = useRouter().resolve(props.to)
      options.href = route.href
    }
    options.tag = computeTag(options)

    // handle some downloadable link (filename, icon...)
    options.href = options.href ?? props.src

    if (props.arrow && !props.icon) {
      options.icon = IconArrow
    }

    if (props.arrow && !props.icon && props.target == '_blank') {
      options.icon = IconBlank
    }

    if (props.download) {
      if (props.download === true && options.href) {
        options.download = last(options.href.split('/'))
        options.target = '_blank'
      }
      if (!props.icon || props.icon !== 'none') options.icon = IconDownload
    }
    return {
      el,
      ...options,
      isSelected: computed(() => props.selected),
      cptTag: computeTag(options),
      cptTheme: computeTheme(options),
      attrTitle: computedTitle(options)
    }
  },
});
export default UiButton


/** Here we could simply use IUiButtonProps
 * bt there is an exemple of how to do without Interface
 */
function computeTheme<T extends { theme?: string, tag?: string }>({ theme, tag }: T): string {
  if (theme) return theme
  if (tag === 'a') return 'link'
  if (tag === 'router-link') return 'primary'
  return 'primary'
}

function computeTag({ tag, to, src, href, /*target, action*/ }: IUiButtonProps): string {
  if (tag) return tag;
  if (to) { return 'router-link'; }

  const link = src || href
  if (link) {
    // if (!target && useRouter().resolve(link)) return 'to'
    return 'a'
  }
  return 'button';
}
function computedTitle({ tag, title, label }: IUiButtonProps): Obj {
  let attr
  if (tag === 'button') {
    attr = { 'aria-label': title || label }
  } else {
    attr = { title: title || label }
  }
  return attr
}
</script>

<template>
  <component :is="cptTag"
             ref="el"
             class="UiButton"
             :data-theme="cptTheme"
             :data-direction="direction"
             :href="href"
             :to="to"
             :target="target"
             :rel="target == '_blank' ? 'noopener' : undefined"
             :download="download || undefined"
             :arrow="arrow || undefined"
             v-bind="attrTitle"
  >
    <span ref="labelEl" class="inner">
      <!-- @slot icon - optional for icon or use prop.icon -->
      <slot name="icon">
        <i v-if="icon"
           ref="iconEl"
           class="icon"
           v-html="isSelected && iconSelected ? iconSelected : icon"
        />
      </slot>

      <!-- @slot default - place for label -->
      <slot v-if="cptTheme != 'icon'">
        <span v-if="label"
              class="label"
              v-text="label"
        />
      </slot>

      <!-- @slot icon - optional for icon or use prop.icon -->

    </span>
    <slot name="extra" />
  </component>
</template>

<style lang="stylus" scoped>
  .UiButton
    display inline-block
    border none
    background none
    margin 0
    padding 0
    outline none
    cursor pointer
    color $c-black
    &[data-direction="left"]
      .icon
        margin-left 0
        rp(margin-right, 22px 15px)
        &:last-child
          margin-right 0
        :deep()
          svg
            transform scaleX(-1)
    
    .inner
      position relative
      z-index 10
      
    .icon
      height 0.9em
      display inline-block
      width auto
      rp(margin-left, 22px 15px)
      &:first-child
        margin-left 0
      :deep()
        svg
          display inline-block
          width auto
</style>
